import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ChangeDetectionStrategy, Component, computed, linkedSignal, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NgxEchartsDirective, provideEchartsCore } from "ngx-echarts";
import * as echarts from "echarts";
import type { EChartsOption } from "echarts";
import { ENGAGEMENT_LABELS } from "./engagement.labels";
import { EngagementService, OrgTimeSeriesPoint, TimeSeriesPoint } from "./engagement.service";
import { s25Resource } from "../../../util/s25.resource";
import { Item } from "../../../pojo/Item";
import { NameService } from "../../../services/name.service";
import { S25Util } from "../../../util/s25-util";
import { UserprefService } from "../../../services/userpref.service";
import { S25Datefilter } from "../../../modules/s25-dateformat/s25.datefilter.service";

type ViewMode = "overall" | "byOrg";
type Bucket = "day" | "week" | "month";

function isoDate(d: Date): string {
    return d.toISOString().slice(0, 10);
}

function formatPeriod(period: string, dateFormat: string): string {
    return S25Datefilter.transform(period, dateFormat) ?? period;
}

function buildOverallOption(points: TimeSeriesPoint[], dateFormat: string): EChartsOption {
    return {
        tooltip: { trigger: "axis" },
        grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
        xAxis: { type: "category", boundaryGap: false, data: points.map((p) => formatPeriod(p.period, dateFormat)) },
        yAxis: { type: "value", name: "Score" },
        series: [
            {
                name: "Engagement",
                type: "line",
                smooth: true,
                data: points.map((p) => p.score),
                areaStyle: {},
            },
        ],
    };
}

function buildByOrgOption(
    points: OrgTimeSeriesPoint[],
    orgNames: Map<number, string>,
    dateFormat: string,
): EChartsOption {
    const periodSet = new Set<string>();
    for (const point of points) periodSet.add(point.period);
    const categories = [...periodSet].sort();

    const scoresByOrg = new Map<number, Map<string, number>>();
    for (const point of points) {
        let periodScores = scoresByOrg.get(point.organizationId);
        if (!periodScores) {
            periodScores = new Map();
            scoresByOrg.set(point.organizationId, periodScores);
        }
        periodScores.set(point.period, point.score);
    }

    const palette = [
        "#4e79a7",
        "#f28e2b",
        "#e15759",
        "#76b7b2",
        "#59a14f",
        "#edc948",
        "#b07aa1",
        "#ff9da7",
        "#9c755f",
        "#bab0ac",
        "#af7aa1",
        "#d37295",
        "#8cd17d",
        "#b6992d",
        "#499894",
        "#f1ce63",
        "#d4a6c8",
        "#86bcb6",
        "#e49444",
        "#d16249",
    ];

    const series = [...scoresByOrg.entries()].map(([orgId, scores], idx) => ({
        name: orgNames.get(orgId) ?? `Organization ${orgId}`,
        type: "line" as const,
        smooth: true,
        data: categories.map((period) => scores.get(period) ?? 0),
        itemStyle: { color: palette[idx % palette.length] },
    }));

    return {
        tooltip: { trigger: "axis" },
        legend: { type: "scroll", bottom: 0 },
        grid: { left: "3%", right: "4%", bottom: "12%", containLabel: true },
        xAxis: { type: "category", boundaryGap: false, data: categories.map((p) => formatPeriod(p, dateFormat)) },
        yAxis: { type: "value", name: "Score" },
        series,
    };
}

@TypeManagerDecorator("s25-ng-se-engagement-dashboard")
@Component({
    selector: "s25-ng-se-engagement-dashboard",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, NgxEchartsDirective],
    providers: [provideEchartsCore({ echarts })],
    template: `
        <div class="se-engagement-dash">
            <h2 class="se-engagement-dash__title">{{ labels.reportTitle }}</h2>

            <div class="se-engagement-dash__toggles" role="tablist">
                <button
                    type="button"
                    class="se-engagement-dash__toggle"
                    [class.se-engagement-dash__toggle--active]="viewMode() === 'overall'"
                    (click)="viewMode.set('overall')"
                >
                    Overall
                </button>
                <button
                    type="button"
                    class="se-engagement-dash__toggle"
                    [class.se-engagement-dash__toggle--active]="viewMode() === 'byOrg'"
                    (click)="viewMode.set('byOrg')"
                >
                    By Organization
                </button>
            </div>

            <div class="se-engagement-dash__filters">
                <label class="se-engagement-dash__field">
                    <span>Start</span>
                    <input class="c-input" type="date" [ngModel]="startDt()" (ngModelChange)="startDt.set($event)" />
                </label>
                <label class="se-engagement-dash__field">
                    <span>End</span>
                    <input class="c-input" type="date" [ngModel]="endDt()" (ngModelChange)="endDt.set($event)" />
                </label>
                <label class="se-engagement-dash__field">
                    <span>Bucket</span>
                    <select class="c-input" [ngModel]="bucket()" (ngModelChange)="onBucketChange($event)">
                        <option value="day">Day</option>
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                    </select>
                </label>
                <button
                    type="button"
                    class="c-textButton se-engagement-dash__refresh"
                    (click)="refresh()"
                    [disabled]="chartLoading()"
                >
                    Refresh
                </button>
            </div>

            @if (chartLoading()) {
                <p class="se-engagement-dash__muted">Loading chart…</p>
            } @else if (chartError()) {
                <p class="se-engagement-dash__error" role="alert">{{ chartError() }}</p>
            } @else {
                <div class="se-engagement-dash__chart" echarts [options]="chartOption()"></div>
            }
        </div>
    `,
    styles: [
        `
            .se-engagement-dash__title {
                margin: 0 0 1rem;
            }
            .se-engagement-dash__toggles {
                display: flex;
                gap: 0.5rem;
                margin-bottom: 1rem;
            }
            .se-engagement-dash__toggle {
                padding: 0.35rem 0.85rem;
                border: 1px solid var(--color--border-bold);
                background: var(--color--surface-bg);
                border-radius: 4px;
                cursor: pointer;
                color: var(--color--text);
            }
            .se-engagement-dash__toggle--active {
                border-color: var(--color--link-text);
                background: var(--color--surface-2-bg);
                font-weight: 600;
            }
            .se-engagement-dash__filters {
                display: flex;
                flex-wrap: wrap;
                gap: 1rem;
                margin-bottom: 1rem;
                align-items: flex-end;
            }
            .se-engagement-dash__refresh {
                align-self: flex-end;
            }
            .se-engagement-dash__field {
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
            }
            .se-engagement-dash__field span {
                font-size: 0.875rem;
            }
            .se-engagement-dash__chart {
                width: 100%;
                height: 420px;
            }
            .se-engagement-dash__muted {
                color: var(--color--text-muted);
            }
            .se-engagement-dash__error {
                color: #c62828;
            }
        `,
    ],
})
export class SeEngagementDashboardComponent {
    readonly labels = ENGAGEMENT_LABELS;

    readonly viewMode = signal<ViewMode>("overall");
    readonly weekstartRes = s25Resource({ loader: () => UserprefService.getWeekstart() });

    readonly startDt = linkedSignal(() => {
        const weekstart = this.weekstartRes.value() ?? 0;
        return isoDate(S25Util.date.firstDayOfWeek(new Date(), weekstart));
    });

    readonly endDt = linkedSignal(() => {
        const weekstart = this.weekstartRes.value() ?? 0;
        return isoDate(S25Util.date.lastDayOfWeek(new Date(), weekstart));
    });

    readonly bucket = signal<Bucket>("day");

    readonly dateFormatRes = s25Resource({ loader: () => UserprefService.getS25Dateformat() });

    readonly hasDates = computed(() => !!this.startDt() && !!this.endDt());

    readonly reportParams = computed(() => ({
        startDt: this.startDt(),
        endDt: this.endDt(),
        bucket: this.bucket(),
        weekstart: this.weekstartRes.value() ?? 0,
    }));

    readonly overallData = s25Resource({
        if: () => this.viewMode() === "overall" && this.hasDates(),
        params: () => this.reportParams(),
        loader: ({ params }) => EngagementService.getEngagementOverTime(params),
    });

    readonly byOrgData = s25Resource({
        if: () => this.viewMode() === "byOrg" && this.hasDates(),
        params: () => this.reportParams(),
        loader: ({ params }) => EngagementService.getEngagementByOrg(params),
    });

    readonly orgIds = computed(() => {
        const points = this.byOrgData.value() ?? [];
        return [...new Set(points.map((point) => point.organizationId))];
    });

    readonly orgNamesRes = s25Resource({
        if: () => this.viewMode() === "byOrg" && this.orgIds().length > 0,
        params: () => this.orgIds(),
        loader: async ({ params: ids }) => {
            const items = ids.map((id) => ({ itemId: id, itemTypeId: Item.Ids.Organization }));
            const nameMap = await NameService.getNames(items);
            return new Map(
                ids.map((id) => {
                    const key = NameService.itemKeyFromIds(id, Item.Ids.Organization);
                    return [id, nameMap[key] ?? `Organization ${id}`];
                }),
            );
        },
    });

    readonly chartLoading = computed(() => {
        if (this.viewMode() === "overall") {
            return this.overallData.resource.isLoading();
        }
        return this.byOrgData.resource.isLoading() || this.orgNamesRes.resource.isLoading();
    });

    readonly chartError = computed(() => {
        const err = this.viewMode() === "overall" ? this.overallData.resource.error() : this.byOrgData.resource.error();
        if (!err) return undefined;
        return err instanceof Error ? err.message : String(err);
    });

    readonly chartOption = computed<EChartsOption>(() => {
        const dateFormat = this.dateFormatRes.value() ?? "";
        if (this.viewMode() === "overall") {
            const pts = this.overallData.value() ?? [];
            if (!pts.length) {
                return { title: { text: "No data", left: "center", top: "middle" } };
            }
            return buildOverallOption(pts, dateFormat);
        }
        const orgPts = this.byOrgData.value() ?? [];
        if (!orgPts.length) {
            return { title: { text: "No data", left: "center", top: "middle" } };
        }
        return buildByOrgOption(orgPts, this.orgNamesRes.value() ?? new Map(), dateFormat);
    });

    onBucketChange(value: string): void {
        if (value === "day" || value === "week" || value === "month") {
            this.bucket.set(value);
        }
    }

    refresh(): void {
        if (this.viewMode() === "overall") {
            this.overallData.resource.reload();
        } else {
            this.byOrgData.resource.reload();
        }
    }
}
